import { IS_DEVELOPMENT } from '@/constants/env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LogContext {
  component?: string;
  action?: string;
  data?: Record<string, unknown>;
  error?: unknown;
  [key: string]: unknown;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

/** Entry stored in the in-memory log buffer (used by the mobile debug viewer). */
export interface CapturedLogEntry {
  id: string;
  timestamp: Date;
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: string;
  args: unknown[];
  source?: string;
}

interface StructuredLogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context: LogContext;
}

// ---------------------------------------------------------------------------
// Level gating
// ---------------------------------------------------------------------------

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
} as const;

const resolveLogLevel = (): LogLevel => {
  const envLevel = (import.meta.env.VITE_LOG_LEVEL ?? '').toLowerCase() as LogLevel;
  if (envLevel && envLevel in LOG_LEVELS) return envLevel;
  return IS_DEVELOPMENT ? 'debug' : 'warn';
};

let currentLogLevel: number = LOG_LEVELS[resolveLogLevel()];

const shouldLog = (level: LogLevel): boolean =>
  LOG_LEVELS[level] >= currentLogLevel;

// ---------------------------------------------------------------------------
// In-memory log capture (replaces ConsoleLoggerService)
// ---------------------------------------------------------------------------

const MAX_CAPTURED_LOGS = 1000;
let capturedLogs: CapturedLogEntry[] = [];
let captureListeners: ((logs: CapturedLogEntry[]) => void)[] = [];
let captureEnabled = false;

const notifyListeners = () => {
  captureListeners.forEach((fn) => {
    try { fn([...capturedLogs]); } catch { /* ignore listener errors */ }
  });
};

const captureLog = (
  level: CapturedLogEntry['level'],
  message: string,
  args: unknown[],
  source?: string,
) => {
  if (!captureEnabled) return;

  const entry: CapturedLogEntry = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date(),
    level,
    message,
    args,
    source,
  };

  capturedLogs.unshift(entry);
  if (capturedLogs.length > MAX_CAPTURED_LOGS) {
    capturedLogs = capturedLogs.slice(0, MAX_CAPTURED_LOGS);
  }
  notifyListeners();
};

// ---------------------------------------------------------------------------
// Structured log formatter + console output
// ---------------------------------------------------------------------------

const formatLogEntry = (
  level: StructuredLogEntry['level'],
  message: string,
  context?: LogContext,
): StructuredLogEntry => ({
  timestamp: new Date().toISOString(),
  level,
  message,
  context: context || {},
});

const CONSOLE_METHODS = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
} as const;

const emitLog = (
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context?: LogContext,
): void => {
  if (!shouldLog(level)) return;

  const logEntry = formatLogEntry(level, message, context);
  const serialized = JSON.stringify(logEntry, null, 2);

  // Write to console
  CONSOLE_METHODS[level](serialized);

  // Capture for debug viewer
  captureLog(
    level === 'debug' ? 'debug' : level,
    message,
    [logEntry],
    context?.component,
  );
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const logger = {
  debug: (message: string, context?: LogContext): void => emitLog('debug', message, context),
  info: (message: string, context?: LogContext): void => emitLog('info', message, context),
  warn: (message: string, context?: LogContext): void => emitLog('warn', message, context),
  error: (message: string, context?: LogContext): void => emitLog('error', message, context),

  /** Change log level at runtime (useful from debug UI or browser console) */
  setLogLevel: (level: LogLevel): void => {
    if (level in LOG_LEVELS) currentLogLevel = LOG_LEVELS[level];
  },

  /** Get the current active log level */
  getLogLevel: (): LogLevel => {
    const entry = Object.entries(LOG_LEVELS).find(([, v]) => v === currentLogLevel);
    return (entry?.[0] ?? 'warn') as LogLevel;
  },

  /** Check if a given level would be logged */
  isEnabled: (level: LogLevel): boolean => shouldLog(level),

  // -- Log capture API (for mobile debug viewer) --

  /** Enable in-memory log capture. Called once by the debug UI when it mounts. */
  enableCapture: (): void => { captureEnabled = true; },

  /** Disable in-memory log capture and free memory. */
  disableCapture: (): void => {
    captureEnabled = false;
    capturedLogs = [];
    captureListeners = [];
  },

  /** Get all captured logs (newest first). */
  getLogs: (): CapturedLogEntry[] => [...capturedLogs],

  /** Clear all captured logs. */
  clearLogs: (): void => {
    capturedLogs = [];
    notifyListeners();
  },

  /** Subscribe to log updates. Returns an unsubscribe function. */
  addListener: (listener: (logs: CapturedLogEntry[]) => void): (() => void) => {
    captureListeners.push(listener);
    return () => {
      captureListeners = captureListeners.filter((fn) => fn !== listener);
    };
  },

  /** Export captured logs as JSON string. */
  exportLogs: (): string => JSON.stringify(capturedLogs, null, 2),

  /** Filter captured logs. */
  filterLogs: (filter: {
    level?: CapturedLogEntry['level'][];
    search?: string;
    source?: string;
  }): CapturedLogEntry[] => {
    let filtered = [...capturedLogs];
    if (filter.level?.length) {
      filtered = filtered.filter((log) => filter.level!.includes(log.level));
    }
    if (filter.search) {
      const q = filter.search.toLowerCase();
      filtered = filtered.filter(
        (log) =>
          log.message.toLowerCase().includes(q) ||
          log.source?.toLowerCase().includes(q),
      );
    }
    if (filter.source) {
      filtered = filtered.filter((log) => log.source?.includes(filter.source!));
    }
    return filtered;
  },

  /** Get log statistics. */
  getLogStats: (): { total: number; byLevel: Record<string, number> } => {
    const stats = { total: capturedLogs.length, byLevel: { log: 0, info: 0, warn: 0, error: 0, debug: 0 } };
    capturedLogs.forEach((log) => { stats.byLevel[log.level]++; });
    return stats;
  },
};